import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { useSite } from "@/context/SiteContext";
import { useTheme } from "@/context/ThemeContext";
import { imgSrc } from "@/lib/api";

export const scrollToContact = () => {
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const WolfeMark = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 34 34" aria-hidden="true">
    <circle cx="17" cy="17" r="15.5" fill="none" stroke="currentColor" strokeWidth="1" />
    <path d="M8 11 L12.5 24 L17 14 L21.5 24 L26 11" fill="none" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

// Grey Wolfe "W" mark — traced from the supplied logo; colour follows currentColor
// (white on teal surfaces, teal on light surfaces).
export const GreyWolfeMark = ({ height = 30, className = "", draw = false }) => (
  <svg height={height} viewBox="116 172 267 156" className={`${draw ? "draw" : ""} ${className}`} role="img" aria-label="Grey Wolfe logo" data-testid="grey-wolfe-mark">
    <g fill="none" stroke="currentColor" strokeWidth="9.5" strokeLinecap="round" strokeLinejoin="round">
      <path pathLength="1" d="M193.1 203 L207.5 179 L123.4 179 L207.5 319.2 L291.6 179 L249.6 179 L215.4 236" />
      <path pathLength="1" d="M249.5 249.2 L291.5 319.2 L375.6 179 L333.6 179 L299.4 236" />
    </g>
  </svg>
);

// Wolfe Realtors skyline mark — traced from the supplied logo; colour follows currentColor.
export const WolfeRealtorsMark = ({ height = 34, className = "", draw = false }) => (
  <svg height={height} viewBox="686 458 627 660" className={`${draw ? "draw slow" : ""} ${className}`} role="img" aria-label="Wolfe Realtors logo" data-testid="wolfe-realtors-mark">
    <path pathLength="1" fill="none" stroke="currentColor" strokeWidth="13" strokeLinejoin="miter" strokeLinecap="butt"
      d="M698 1116 V742.2 L895.5 628.6 V1110 H833.5 V756.1 L1030.5 869 V1110 H968.5 V472.2 L1165.5 586.5 V1110 H1103.5 V982.5 L1301 869.9 V1116" />
  </svg>
);

const BRANDS = {
  group: { Mark: GreyWolfeMark, name: "GREY WOLFE", sub: null },
  "oil-gas": { Mark: GreyWolfeMark, name: "GREY WOLFE", sub: "Limited" },
  development: { Mark: GreyWolfeMark, name: "GREY WOLFE", sub: "Development" },
  realty: { Mark: WolfeRealtorsMark, name: "WOLFE", sub: "Realtors" },
};

const ARM_ALIAS = { shortlets: "realty", booking: "realty" };

export const armFromPath = (pathname) => {
  const raw = pathname.split("/")[1];
  const seg = ARM_ALIAS[raw] || raw;
  return BRANDS[seg] ? seg : "group";
};

export const BrandLogo = ({ small = false, arm = "group" }) => {
  const { settings } = useSite();
  const { resolved } = useTheme();
  const src = (resolved === "dark" && settings.logo_dark) || settings.logo;
  const showName = !src || settings.logo_show_name !== false;
  const { Mark, name, sub } = BRANDS[arm];
  return (
    <span className={`flex items-center gap-3 ${small ? "" : "h-10"}`} data-testid="brand-logo">
      {src ? (
        <img src={imgSrc(src)} alt="Grey Wolfe" className={`${small ? "h-8" : "h-9 sm:h-10"} w-auto max-w-[180px] object-contain`} data-testid="brand-logo-image" />
      ) : Mark === GreyWolfeMark ? (
        <GreyWolfeMark key={arm} draw={!small} height={small ? 22 : 28} />
      ) : (
        <Mark key={arm} draw={!small} height={small ? 26 : 34} />
      )}
      {showName && (
        <span className="flex flex-col leading-none" data-testid={`brand-name-${arm}`}>
          <span className={`lux tracking-[.08em] whitespace-nowrap ${small ? "text-lg" : "text-xl"}`}>{name}</span>
          {sub && <span className="lab text-[10px] tracking-[.32em] text-gw-mist mt-1">{sub}</span>}
        </span>
      )}
    </span>
  );
};

// Large line-drawn company logo shown at the top of each company page.
export const ArmIntroMark = ({ arm }) => {
  const { Mark } = BRANDS[arm];
  const names = { "oil-gas": "Grey Wolfe Limited", realty: "Wolfe Realtors", development: "Grey Wolfe Development" };
  return (
    <div className="flex items-center gap-5 mt-12" data-testid={`arm-intro-mark-${arm}`}>
      <Mark draw height={Mark === GreyWolfeMark ? 56 : 72} />
      <span className="draw-fade lab opacity-80">{names[arm]}</span>
    </div>
  );
};

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/oil-gas", label: "Grey Wolfe Limited", id: "oil-gas" },
  { to: "/realty", label: "Wolfe Realtors", id: "realty" },
  { to: "/shortlets", label: "Shortlets", id: "shortlets" },
  { to: "/development", label: "Grey Wolfe Development", id: "development" },
];

const linkCls = ({ isActive }) =>
  `relative py-1 transition-opacity duration-200 hover:opacity-100 ${isActive ? "opacity-100 after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-px after:bg-white" : "opacity-75"}`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-gw-ground/95 backdrop-blur-md text-white border-b border-white/20">
      <nav className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <BrandLogo arm={armFromPath(pathname)} />
        </Link>
        <div className="hidden lg:flex gap-8 lab">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end data-testid={`nav-${l.id}-link`} className={linkCls}>{l.label}</NavLink>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button type="button" data-testid="header-enquire-button" onClick={scrollToContact}
            className="hidden sm:inline-block lab bg-white text-gw-onwhite px-4 py-2 transition-colors duration-200 hover:bg-gw-mist">
            Enquire
          </button>
          <button type="button" aria-label="Menu" data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden p-1">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/20 bg-gw-deep">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.id}-link`}
              className={`block px-6 py-4 lab border-b border-white/10 ${pathname === l.to ? "text-white" : "text-gw-pale"}`}>
              {l.label}
            </Link>
          ))}
          <button type="button" data-testid="mobile-enquire-button" onClick={() => { setOpen(false); scrollToContact(); }}
            className="block w-full text-left px-6 py-4 lab bg-white text-gw-onwhite">
            Enquire →
          </button>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-gw-deep text-gw-pale py-12">
    <div className="max-w-6xl mx-auto px-6 flex flex-wrap justify-between gap-4 lab">
      <span>Grey Wolfe Limited · Wolfe Realtors · Grey Wolfe Development</span>
      <span className="flex gap-6">
        <span>Local &amp; international</span>
        <Link to="/admin" data-testid="footer-admin-link" className="opacity-60 hover:opacity-100 transition-opacity">Staff login</Link>
      </span>
    </div>
  </footer>
);

const ROMAN = { "oil-gas": "I", realty: "II", development: "III" };
const ARM_NAMES = { "oil-gas": "Grey Wolfe Limited", realty: "Wolfe Realtors", development: "Grey Wolfe Development" };
const SHORT = { "oil-gas": "Grey Wolfe Limited", realty: "Wolfe Realtors", development: "Development" };

export const ArmBar = ({ current, light = false }) => (
  <div className={`flex flex-wrap justify-between gap-4 lab border-b pb-4 ${light ? "border-gw-rule/25" : "text-gw-mist border-white/25"}`}>
    <span data-testid="arm-bar-title">Page {ROMAN[current]} · {ARM_NAMES[current]}</span>
    <span className="flex gap-6">
      <Link to="/" data-testid="arm-bar-group-link" className="hover:underline underline-offset-4">← Group</Link>
      {Object.keys(ARM_NAMES).filter((k) => k !== current).map((k) => (
        <Link key={k} to={`/${k}`} data-testid={`arm-bar-${k}-link`} className="hover:underline underline-offset-4">{SHORT[k]}</Link>
      ))}
    </span>
  </div>
);
